"use client";

import { useMemo, useState } from "react";
import type { GoalItem, GoalFocusPriority } from "@/lib/api";

type Option = {
  key: GoalFocusPriority;
  label: string;
};

const OPTIONS: Option[] = [
  { key: "primary", label: "Primary" },
  { key: "secondary", label: "Secondary" },
  { key: "background", label: "Background" },
];

interface Props {
  goal: GoalItem;
  // The other active goals in the same Life Area. Used to compute the
  // cascade-preview line ("Promoting will move 'Read 20 books' from Primary
  // → Secondary in Learning"). Server enforces the cascade; this is purely
  // informational.
  peersInArea: GoalItem[];
  disabled?: boolean;
  onChange: (next: GoalFocusPriority) => Promise<void> | void;
}

function areaLabel(category: string | undefined): string {
  if (!category) return "this area";
  return category.charAt(0).toUpperCase() + category.slice(1);
}

export default function GoalFocusToggle({
  goal,
  peersInArea,
  disabled,
  onChange,
}: Props) {
  const [pending, setPending] = useState<GoalFocusPriority | null>(null);
  const [hovered, setHovered] = useState<GoalFocusPriority | null>(null);

  const current: GoalFocusPriority = goal.focus_priority ?? "background";
  const noCategory = !goal.category;

  const cascadePreview = useMemo(() => {
    const target = hovered ?? null;
    if (!target || target === current) return null;
    if (target === "background") return null;
    const holder = peersInArea.find(
      (p) => p.id !== goal.id && (p.focus_priority ?? "background") === target
    );
    if (!holder) return null;
    const demoteTo = target === "primary" ? "Secondary" : "Background";
    return `Promoting will move "${holder.title}" to ${demoteTo} in ${areaLabel(goal.category)}.`;
  }, [hovered, current, peersInArea, goal.id, goal.category]);

  const restingHint = (() => {
    if (noCategory) {
      return "Set a Life Area before promoting this goal.";
    }
    if (current === "background") {
      return `Promote to Primary or Secondary to give this goal a bigger share of ${areaLabel(goal.category)}.`;
    }
    if (current === "primary") {
      return `Primary in ${areaLabel(goal.category)} — gets the biggest share of weekly time.`;
    }
    return `Secondary in ${areaLabel(goal.category)} — protected, but Primary gets more time.`;
  })();

  const handleClick = async (next: GoalFocusPriority) => {
    if (next === current || pending || noCategory) return;
    setPending(next);
    try {
      await onChange(next);
    } finally {
      setPending(null);
    }
  };

  return (
    <div data-testid={`goal-focus-toggle-${goal.id}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
        Focus in {areaLabel(goal.category)}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {OPTIONS.map((option) => {
          const isActive = option.key === current;
          const isLoading = pending === option.key;
          return (
            <button
              key={option.key}
              type="button"
              data-testid={`goal-focus-${goal.id}-${option.key}`}
              aria-pressed={isActive}
              disabled={disabled || noCategory || Boolean(pending)}
              onMouseEnter={() => setHovered(option.key)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(option.key)}
              onBlur={() => setHovered(null)}
              onClick={() => void handleClick(option.key)}
              className={`rounded-full px-3 py-1.5 text-[10px] font-semibold transition-colors ${
                isActive
                  ? "bg-forest-med text-white"
                  : "border border-border bg-white text-text-light hover:bg-sage-pale"
              } ${isLoading ? "cursor-wait opacity-70" : ""} ${
                noCategory ? "cursor-not-allowed opacity-60" : ""
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-muted">
        {cascadePreview ?? restingHint}
      </p>
    </div>
  );
}
